import type { Dispatch } from "@reduxjs/toolkit"
import type { RootState } from "@/store"
import { TAGS } from "@/store/api"
import { deckEndpoints } from "@/store/apis/deckAPI"
import { effectsEndpoints } from "@/store/apis/effectsAPI"
import {
  GAMEPLAY_ACTION_WATCHDOG_MS,
  GAMEPLAY_DECK_INVARIANT_DEBOUNCE_MS,
  GAMEPLAY_EXPECTED_HAND_SIZE,
  GAMEPLAY_HEARTBEAT_INTERVAL_MS,
  GAMEPLAY_STATE_STALE_MS,
  setLastStateReceivedAt
} from "@/store/slices/gameplay"
import { gameplayEndpoints } from "./gameplay.api"

interface IResilienceDeps {
  dispatch: Dispatch
  getState: () => RootState
}

const isHealthyConnection = (state: RootState): boolean => {
  if (typeof navigator !== "undefined" && navigator.onLine === false)
    return false
  if (state.gameplay.isSocketConnected === false) return false
  return true
}

const invalidateGameplayCaches = (dispatch: Dispatch) => {
  dispatch(gameplayEndpoints.util.invalidateTags([{ type: TAGS.GAMEPLAY }]))
  dispatch(deckEndpoints.util.invalidateTags([{ type: TAGS.DECK }]))
  dispatch(effectsEndpoints.util.invalidateTags([{ type: TAGS.EFFECTS }]))
}

let deps: IResilienceDeps | null = null
let deckInvariantTimer: ReturnType<typeof setTimeout> | null = null
let heartbeatTimer: ReturnType<typeof setInterval> | null = null

const clearDeckInvariantTimer = () => {
  if (deckInvariantTimer) {
    clearTimeout(deckInvariantTimer)
    deckInvariantTimer = null
  }
}

const clearHeartbeatTimer = () => {
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer)
    heartbeatTimer = null
  }
}

const checkDeckHandInvariant = () => {
  clearDeckInvariantTimer()

  if (!deps) return

  const state = deps.getState()
  const { sessionId } = state.gameplay
  if (!sessionId) return

  // RTK's enhanceEndpoints returns a new api type; the store's RootState is
  // built from the base api, so the tag-shape doesn't match the selector's
  // expected state. Cast at the boundary — runtime shape is identical.
  const handData = deckEndpoints.endpoints.getDeckHand.select(sessionId)(
    state as never
  ).data

  if (handData === undefined) return
  if (handData.length === GAMEPLAY_EXPECTED_HAND_SIZE) return

  const handLength = handData.length
  deckInvariantTimer = setTimeout(() => {
    deckInvariantTimer = null
    if (!deps) return

    const latestState = deps.getState()
    if (latestState.gameplay.isMatchFinished) return
    if (!isHealthyConnection(latestState)) return

    console.warn(
      `[gameplay-resilience] hand invariant violated ${GAMEPLAY_DECK_INVARIANT_DEBOUNCE_MS}ms — refetching deck (had length=${handLength})`
    )
    deps.dispatch(deckEndpoints.util.invalidateTags([{ type: TAGS.DECK }]))
  }, GAMEPLAY_DECK_INVARIANT_DEBOUNCE_MS)
}

const runHeartbeatTick = () => {
  if (!deps) return

  const state = deps.getState()
  if (state.gameplay.isMatchFinished) return
  if (!isHealthyConnection(state)) return

  checkDeckHandInvariant()

  const { lastStateReceivedAt } = state.gameplay
  if (lastStateReceivedAt === 0) return

  const silenceMs = Date.now() - lastStateReceivedAt
  if (silenceMs <= GAMEPLAY_STATE_STALE_MS) return

  console.warn(
    `[gameplay-resilience] state-stale watchdog: no state event for ${silenceMs}ms — refetching gameplay/deck/effects`
  )
  invalidateGameplayCaches(deps.dispatch)
  deps.dispatch(setLastStateReceivedAt(Date.now()))
}

export const gameplayResilience = {
  start(nextDeps: IResilienceDeps) {
    deps = nextDeps
    if (!heartbeatTimer) {
      heartbeatTimer = setInterval(
        runHeartbeatTick,
        GAMEPLAY_HEARTBEAT_INTERVAL_MS
      )
    }
  },

  stop() {
    clearHeartbeatTimer()
    clearDeckInvariantTimer()
    deps = null
  },

  onStateReceived() {
    checkDeckHandInvariant()
  },

  armPostActionWatchdog(cardId: string | null | undefined) {
    if (!deps) return

    const actionAt = Date.now()
    const localDeps = deps

    setTimeout(() => {
      if (deps !== localDeps) return

      const state = deps.getState()
      if (state.gameplay.isMatchFinished) return
      if (state.gameplay.lastStateReceivedAt >= actionAt) return
      if (!isHealthyConnection(state)) return

      console.warn(
        `[gameplay-resilience] post-action watchdog: no state event within ${GAMEPLAY_ACTION_WATCHDOG_MS}ms — refetching (cardId=${cardId})`
      )
      invalidateGameplayCaches(deps.dispatch)
    }, GAMEPLAY_ACTION_WATCHDOG_MS)
  }
}
